import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import SEO from "@/components/base/SEO";
import "./landing.css";

const SITE = "https://payflow.aryantechworld.com";
const OG_IMAGE = `${SITE}/og-image.png`;

const API = import.meta.env.VITE_API_BASE_URL || "https://api-payflow.aryantechworld.com";

interface Suggestion {
  id: string;
  name: string;
  message: string;
  created_at: string;
}

export default function Landing() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  // Suggestions state
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [sugLoading, setSugLoading] = useState(true);
  const [sugName, setSugName] = useState("");
  const [sugMessage, setSugMessage] = useState("");
  const [sugSubmitting, setSugSubmitting] = useState(false);
  const [sugSuccess, setSugSuccess] = useState(false);
  const [sugError, setSugError] = useState("");

  const fetchSuggestions = async () => {
    try {
      const res = await fetch(`${API}/api/v1/suggestions`);
      const data = await res.json();
      setSuggestions(Array.isArray(data) ? data : []);
    } catch {
      // silently fail
    } finally {
      setSugLoading(false);
    }
  };

  const submitSuggestion = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (!sugName.trim() || !sugMessage.trim()) {
      setSugError("Please fill in your name and suggestion.");
      return;
    }
    setSugSubmitting(true);
    setSugError("");
    try {
      const res = await fetch(`${API}/api/v1/suggestions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: sugName.trim(), message: sugMessage.trim() }),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.detail || "Failed to submit");
      }
      const newSug = await res.json();
      setSuggestions((prev) => [newSug, ...prev]);
      setSugName("");
      setSugMessage("");
      setSugSuccess(true);
      setTimeout(() => setSugSuccess(false), 3000);
    } catch (err) {
      setSugError(err instanceof Error ? err.message : "Failed to submit suggestion.");
    } finally {
      setSugSubmitting(false);
    }
  };

  useEffect(() => { fetchSuggestions(); }, []);

  // FAQ accordion
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const faqs = [
    { q: "Is PayFlow AI free to use?", a: "Yes! The free plan lets you track up to 100 payments and includes email & push reminders, calendar sync, and 6 language support. No credit card required." },
    { q: "How does auto-recurring work?", a: "When you mark a payment as Paid, PayFlow AI automatically creates the next occurrence with the correct due date. For Monthly payments it advances by 1 month, Weekly by 7 days, and so on — handling edge cases like February 28/29 correctly." },
    { q: "Can I track EMIs with a fixed end date?", a: "Absolutely. Set an End Date when adding a recurring payment. PayFlow AI stops creating new entries after that date and shows a 'Last Payment' badge on the final instalment." },
    { q: "What notification channels are supported?", a: "PayFlow AI sends reminders via push notifications, email, SMS, and WhatsApp. You can configure which channels and how many days before the due date (7 days, 3 days, 1 day, same day, and overdue alerts)." },
    { q: "Does it work on Android?", a: "Yes — download the APK directly. The app is built with Capacitor and runs natively on Android. iOS support is coming soon." },
    { q: "Can I sync with Google Calendar?", a: "Yes. Go to the Calendar page and tap Export ICS to download a calendar file, or use the Add to Google Calendar button on any payment card. Events include repeat rules so they appear every cycle automatically." },
    { q: "Is my financial data secure?", a: "All data is transmitted over HTTPS and stored in an encrypted PostgreSQL database. We never store card numbers or banking credentials — PayFlow AI is a reminder tool, not a payment processor." },
    { q: "What is the Pro plan?", a: "Pro gives you unlimited payments, unlimited subscriptions, SMS + WhatsApp alerts, 1GB document storage, and advanced analytics for ₹199/month." },
  ];

  // On native Android/iOS, skip the marketing page and go straight to the app
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      navigate("/home", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) return; // skip animations if redirecting
    // Scroll-triggered fade-up animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, i) => {
          if (entry.isIntersecting) {
            setTimeout(() => entry.target.classList.add("visible"), i * 80);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll(".lp-fade-up").forEach((el) => observer.observe(el));

    // Stat counter animation
    function animateCount(el: Element, target: number, suffix = "") {
      const duration = 1500;
      const start = performance.now();
      const update = (time: number) => {
        const progress = Math.min((time - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        (el as HTMLElement).textContent = Math.floor(eased * target).toLocaleString() + suffix;
        if (progress < 1) requestAnimationFrame(update);
      };
      requestAnimationFrame(update);
    }

    const statsObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            statsObserver.unobserve(entry.target);
            const nums = entry.target.querySelectorAll(".lp-stat-num");
            if (nums[0]) animateCount(nums[0], 2000, "+");
            if (nums[1]) (nums[1] as HTMLElement).textContent = "₹50Cr+";
            if (nums[2]) animateCount(nums[2], 6, "");
            if (nums[3]) (nums[3] as HTMLElement).textContent = "99.9%";
          }
        });
      },
      { threshold: 0.5 }
    );
    const statsEl = document.querySelector(".lp-stats");
    if (statsEl) statsObserver.observe(statsEl);

    return () => {
      observer.disconnect();
      statsObserver.disconnect();
    };
  }, []);

  const scrollTo = (id: string) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const schema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "PayFlow AI",
    "url": SITE,
    "description": "Smart payment reminders, subscription tracking, EMI auto-recurring, and calendar sync in one app.",
    "applicationCategory": "FinanceApplication",
    "operatingSystem": "Android, Web",
    "offers": [
      { "@type": "Offer", "price": "0", "priceCurrency": "INR", "name": "Free Plan" },
      { "@type": "Offer", "price": "199", "priceCurrency": "INR", "name": "Pro Plan" }
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "2000"
    },
    "publisher": { "@type": "Organization", "name": "Aryan Tech World", "url": SITE }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(f => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a }
    }))
  };

  return (
    <div className="lp">
      <SEO
        title="PayFlow AI — Never Miss a Payment Again"
        description="Track bills, subscriptions, and EMIs. Get smart reminders before due dates. Auto-recurring payments, calendar sync, and analytics — free forever plan."
        canonicalPath="/"
        ogImage={OG_IMAGE}
        schema={schema}
      />
      {/* FAQ schema injected separately */}
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      {/* ── Navbar ── */}
      <nav className="lp-nav">
        <div className="lp-container">
          <div className="lp-nav-inner">
            <a href="#" className="lp-logo">
              <div className="lp-logo-icon"><i className="ri-money-dollar-circle-fill"></i></div>
              <span className="lp-logo-text">PayFlow <span>AI</span></span>
            </a>
            <ul className="lp-nav-links">
              <li><a href="#features" onClick={(e) => { e.preventDefault(); scrollTo("features"); }}>Features</a></li>
              <li><a href="#how-it-works" onClick={(e) => { e.preventDefault(); scrollTo("how-it-works"); }}>How it Works</a></li>
              <li><a href="#pricing" onClick={(e) => { e.preventDefault(); scrollTo("pricing"); }}>Pricing</a></li>
              <li><a href="#testimonials" onClick={(e) => { e.preventDefault(); scrollTo("testimonials"); }}>Reviews</a></li>
              <li><a href="#faq" onClick={(e) => { e.preventDefault(); scrollTo("faq"); }}>FAQ</a></li>
              <li><a href="#" onClick={(e) => { e.preventDefault(); navigate("/contact"); }}>Contact</a></li>
            </ul>
            <div className="lp-nav-cta">
              <button className="lp-btn lp-btn-outline" style={{ padding: "10px 20px", fontSize: "13px" }} onClick={() => navigate("/home")}>
                Open App
              </button>
              <button className="lp-btn lp-btn-primary" style={{ padding: "10px 20px", fontSize: "13px" }} onClick={() => scrollTo("download")}>
                <i className="ri-download-line"></i>Get Started
              </button>
            </div>
            <button className="lp-hamburger" onClick={() => setMenuOpen(!menuOpen)}>
              <i className={menuOpen ? "ri-close-line" : "ri-menu-line"}></i>
            </button>
          </div>
          <div className={`lp-mobile-menu${menuOpen ? " open" : ""}`}>
            <a href="#features" onClick={(e) => { e.preventDefault(); scrollTo("features"); }}>Features</a>
            <a href="#how-it-works" onClick={(e) => { e.preventDefault(); scrollTo("how-it-works"); }}>How it Works</a>
            <a href="#pricing" onClick={(e) => { e.preventDefault(); scrollTo("pricing"); }}>Pricing</a>
            <a href="#testimonials" onClick={(e) => { e.preventDefault(); scrollTo("testimonials"); }}>Reviews</a>
            <a href="#" style={{ color: "#4f46e5", fontWeight: 700 }} onClick={(e) => { e.preventDefault(); navigate("/home"); }}>Open App →</a>
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="lp-hero">
        <div className="lp-container">
          <div className="lp-hero-grid">
            <div className="lp-fade-up">
              <span className="lp-badge"><i className="ri-sparkling-2-line"></i>AI-Powered Payment Tracker</span>
              <h1 className="lp-hero-title">Never Miss a<br /><span className="lp-gradient-text">Payment Again</span></h1>
              <p className="lp-hero-sub">Track bills, subscriptions, and EMIs in one place. Get reminders before they're due. Let PayFlow AI handle the rest — automatically.</p>
              <div className="lp-hero-actions">
                <button className="lp-btn lp-btn-primary" onClick={() => navigate("/auth/signup")}>
                  <i className="ri-user-add-line"></i>Start Free
                </button>
                <button className="lp-btn lp-btn-outline" onClick={() => navigate("/home")}>
                  <i className="ri-layout-grid-line"></i>View Dashboard
                </button>
              </div>
              <div className="lp-social-proof">
                <div className="lp-avatar-stack">
                  {[
                    { letter: "R", bg: "linear-gradient(135deg,#4f46e5,#7c3aed)" },
                    { letter: "S", bg: "linear-gradient(135deg,#7c3aed,#db2777)" },
                    { letter: "A", bg: "linear-gradient(135deg,#0ea5e9,#6366f1)" },
                    { letter: "M", bg: "linear-gradient(135deg,#10b981,#3b82f6)" },
                  ].map((a) => (
                    <div key={a.letter} className="lp-avatar" style={{ background: a.bg }}>{a.letter}</div>
                  ))}
                </div>
                <div>
                  <div className="lp-stars">★★★★★</div>
                  <p className="lp-social-text"><strong>2,000+</strong> users tracking smarter</p>
                </div>
              </div>
            </div>

            <div className="lp-hero-visual">
              <div className="lp-phone-wrap">
                <div className="lp-float-card lp-fc1">
                  <div className="lp-fc-icon">🔔</div>
                  <div className="lp-fc-title">Rent Due in 3 days</div>
                  <div className="lp-fc-sub">₹15,000 · Monthly</div>
                </div>
                <div className="lp-float-card lp-fc2">
                  <div className="lp-fc-big">₹ 42,500</div>
                  <div className="lp-fc-sub">Total this month</div>
                </div>
                <div className="lp-float-card lp-fc3">
                  <div className="lp-fc-icon">✅</div>
                  <div className="lp-fc-title">Auto-created!</div>
                  <div className="lp-fc-sub">Next EMI · June 2026</div>
                </div>
                <div className="lp-phone">
                  <div className="lp-phone-notch"></div>
                  <div className="lp-phone-screen">
                    <div className="lp-s-header">
                      <div className="lp-s-greeting">Good morning 👋</div>
                      <div className="lp-s-name">Aryan Kumar</div>
                      <div className="lp-s-card">
                        <div className="lp-s-amount-label">Due This Month</div>
                        <div className="lp-s-amount">₹42,500</div>
                        <div className="lp-s-amount-sub">4 of 7 payments pending</div>
                      </div>
                    </div>
                    <div className="lp-s-body">
                      <div className="lp-s-sec">Upcoming</div>
                      {[
                        { icon: "ri-home-3-line", iconBg: "#fee2e2", iconColor: "#dc2626", title: "House Rent", sub: "Monthly · Jun 1", amt: "₹15,000", badge: "Overdue", badgeBg: "#fee2e2", badgeColor: "#dc2626" },
                        { icon: "ri-flashlight-line", iconBg: "#fef3c7", iconColor: "#d97706", title: "Electricity", sub: "Monthly · Jun 5", amt: "₹3,200", badge: "3 days", badgeBg: "#fef3c7", badgeColor: "#d97706" },
                        { icon: "ri-netflix-fill", iconBg: "#dbeafe", iconColor: "#2563eb", title: "Netflix", sub: "Monthly · Jun 10", amt: "₹649", badge: "Upcoming", badgeBg: "#d1fae5", badgeColor: "#059669" },
                        { icon: "ri-bank-card-line", iconBg: "#f5f3ff", iconColor: "#7c3aed", title: "Phone EMI", sub: "Jun 15 · 6 left", amt: "₹4,200", badge: "Upcoming", badgeBg: "#d1fae5", badgeColor: "#059669" },
                      ].map((row) => (
                        <div key={row.title} className="lp-s-row">
                          <div className="lp-s-icon" style={{ background: row.iconBg }}>
                            <i className={row.icon} style={{ color: row.iconColor }}></i>
                          </div>
                          <div className="lp-s-info">
                            <div className="lp-s-ptitle">{row.title}</div>
                            <div className="lp-s-psub">{row.sub}</div>
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <div className="lp-s-amt">{row.amt}</div>
                            <div className="lp-s-badge" style={{ background: row.badgeBg, color: row.badgeColor }}>{row.badge}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats ── */}
      <section className="lp-stats">
        <div className="lp-container">
          <div className="lp-stats-grid">
            {[
              { num: "2,000+", label: "Active Users" },
              { num: "₹50Cr+", label: "Payments Tracked" },
              { num: "6", label: "Languages Supported" },
              { num: "99.9%", label: "Uptime" },
            ].map((s) => (
              <div key={s.label} className="lp-stat lp-fade-up">
                <div className="lp-stat-num lp-gradient-text">{s.num}</div>
                <div className="lp-stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="lp-features" id="features">
        <div className="lp-container">
          <div className="lp-fade-up" style={{ textAlign: "center" }}>
            <div className="lp-section-label">Features</div>
            <h2 className="lp-section-title">Everything you need to stay on top of payments</h2>
            <p className="lp-section-sub" style={{ margin: "14px auto 0" }}>A complete financial toolkit — from bill reminders to subscription tracking to calendar sync.</p>
          </div>
          <div className="lp-features-grid">
            {[
              { bg: "#eef2ff", icon: "ri-alarm-line", color: "#4f46e5", title: "Smart Reminders", desc: "Get notified 7 days, 3 days, 1 day, and on the same day via push, email, SMS, or WhatsApp — before you miss anything." },
              { bg: "#f0fdf4", icon: "ri-refresh-line", color: "#16a34a", title: "Auto-Recurring", desc: "Mark a payment as paid and the next cycle is created automatically. Supports Daily, Weekly, Monthly, Quarterly, and Yearly." },
              { bg: "#fff7ed", icon: "ri-timer-line", color: "#ea580c", title: "EMI Tracking", desc: "Set an end date on any recurring payment. PayFlow AI stops auto-creating entries after the last instalment — perfect for EMIs." },
              { bg: "#fdf4ff", icon: "ri-bar-chart-grouped-line", color: "#9333ea", title: "Analytics & Insights", desc: "Visual spending breakdowns by category, monthly trends, paid vs pending summaries — all at a glance." },
              { bg: "#ecfeff", icon: "ri-calendar-2-line", color: "#0891b2", title: "Calendar Sync", desc: "Export all your payments to Google Calendar or download an ICS file. One tap — all due dates appear in your calendar app." },
              { bg: "#fff1f2", icon: "ri-file-list-3-line", color: "#e11d48", title: "Document Storage", desc: "Upload invoices, bills, and receipts. Keep everything in one place — PDFs, images, and documents linked to payments." },
            ].map((f) => (
              <div key={f.title} className="lp-feat-card lp-fade-up">
                <div className="lp-feat-icon" style={{ background: f.bg }}>
                  <i className={f.icon} style={{ color: f.color }}></i>
                </div>
                <div className="lp-feat-title">{f.title}</div>
                <p className="lp-feat-desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it Works ── */}
      <section className="lp-how" id="how-it-works">
        <div className="lp-container">
          <div className="lp-fade-up" style={{ textAlign: "center" }}>
            <div className="lp-section-label">How it Works</div>
            <h2 className="lp-section-title">Up and running in 60 seconds</h2>
            <p className="lp-section-sub" style={{ margin: "14px auto 0" }}>No spreadsheets. No complex setup. Just add your bills and PayFlow AI handles the rest.</p>
          </div>
          <div className="lp-steps">
            {[
              { n: "1", title: "Add Your Payments", desc: "Enter your bills, subscriptions, or EMIs — title, amount, due date, and frequency. Takes 30 seconds per payment." },
              { n: "2", title: "Get Reminded", desc: "Receive timely alerts via push notification, email, SMS, or WhatsApp before each payment is due." },
              { n: "3", title: "Mark Paid & Repeat", desc: "Tap Mark Paid. For recurring payments, the next entry is created automatically — no re-entry needed ever." },
            ].map((s) => (
              <div key={s.n} className="lp-step lp-fade-up">
                <div className="lp-step-num">{s.n}</div>
                <div className="lp-step-title">{s.title}</div>
                <p className="lp-step-desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Recurring Deep Dive ── */}
      <section className="lp-recurring">
        <div className="lp-container">
          <div className="lp-recurring-grid">
            <div className="lp-fade-up">
              <div className="lp-section-label">Auto-Recurring</div>
              <h2 className="lp-section-title">Set it once.<br />Track it forever.</h2>
              <ul className="lp-rec-list">
                {[
                  { strong: "Monthly bills auto-renew", text: " — Rent, electricity, internet — never add them twice." },
                  { strong: "EMI with end dates", text: " — Set your phone or car loan to stop after the last instalment. Automatically." },
                  { strong: "Smart date handling", text: " — Feb 28, month-end edge cases handled correctly. Always." },
                  { strong: "Free plan limit aware", text: " — Auto-creation respects your plan limits, never silently fails." },
                ].map((item) => (
                  <li key={item.strong}>
                    <div className="lp-check"><i className="ri-check-line"></i></div>
                    <p><strong>{item.strong}</strong>{item.text}</p>
                  </li>
                ))}
              </ul>
            </div>
            <div className="lp-rec-visual lp-fade-up">
              {[
                { iconBg: "#fee2e2", icon: "ri-home-3-line", iconColor: "#dc2626", title: "House Rent", sub: "Monthly · No end date", badge: "Recurring forever", badgeBg: "#eff6ff", badgeColor: "#2563eb", amt: "₹15,000" },
                { iconBg: "#f5f3ff", icon: "ri-bank-card-line", iconColor: "#7c3aed", title: "Phone EMI", sub: "Monthly · Ends Dec 2026", badge: "6 remaining", badgeBg: "#f5f3ff", badgeColor: "#7c3aed", amt: "₹4,200" },
                { iconBg: "#dbeafe", icon: "ri-netflix-fill", iconColor: "#2563eb", title: "Netflix", sub: "Monthly · No end date", badge: "Recurring forever", badgeBg: "#eff6ff", badgeColor: "#2563eb", amt: "₹649" },
              ].map((c) => (
                <div key={c.title} className="lp-rec-card">
                  <div className="lp-rec-icon" style={{ background: c.iconBg }}>
                    <i className={c.icon} style={{ color: c.iconColor }}></i>
                  </div>
                  <div className="lp-rec-info">
                    <div className="lp-rec-title">{c.title}</div>
                    <div className="lp-rec-sub">{c.sub}</div>
                    <span className="lp-rec-badge" style={{ background: c.badgeBg, color: c.badgeColor }}>{c.badge}</span>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div className="lp-rec-amt">{c.amt}</div>
                  </div>
                </div>
              ))}
              <div className="lp-rec-auto">
                <i className="ri-magic-line" style={{ color: "#4f46e5", fontSize: "14px" }}></i>
                <p className="lp-rec-auto-text">After marking June paid → July entry created automatically</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section className="lp-pricing" id="pricing">
        <div className="lp-container">
          <div className="lp-fade-up" style={{ textAlign: "center" }}>
            <div className="lp-section-label">Pricing</div>
            <h2 className="lp-section-title">Simple, transparent pricing</h2>
            <p className="lp-section-sub" style={{ margin: "14px auto 0" }}>Start free. Upgrade when you need more. No hidden fees.</p>
          </div>
          <div className="lp-pricing-grid">
            <div className="lp-price-card lp-fade-up">
              <div className="lp-plan-name">Free</div>
              <div className="lp-plan-price"><span>₹</span>0</div>
              <div className="lp-plan-period">Forever free</div>
              <p className="lp-plan-desc">Perfect for personal bills and getting started.</p>
              <ul className="lp-plan-features">
                {["Up to 100 payments", "Subscription tracking", "Email & push reminders", "Calendar sync (ICS)", "6 languages", "Auto-recurring payments"].map((f) => (
                  <li key={f}><i className="ri-check-line"></i>{f}</li>
                ))}
                {["Unlimited payments", "Priority support"].map((f) => (
                  <li key={f} style={{ color: "#cbd5e1" }}><i className="ri-close-line" style={{ color: "#cbd5e1" }}></i>{f}</li>
                ))}
              </ul>
              <div className="lp-plan-cta">
                <button className="lp-btn lp-btn-outline" style={{ width: "100%", justifyContent: "center" }} onClick={() => navigate("/auth/signup")}>
                  Get Started Free
                </button>
              </div>
            </div>
            <div className="lp-price-card popular lp-fade-up">
              <div className="lp-popular-badge">⭐ Most Popular</div>
              <div className="lp-plan-name">Pro</div>
              <div className="lp-plan-price"><span>₹</span>199</div>
              <div className="lp-plan-period">per month</div>
              <p className="lp-plan-desc">For power users managing many payments and subscriptions.</p>
              <ul className="lp-plan-features">
                {["Unlimited payments", "Unlimited subscriptions", "All notification channels", "SMS + WhatsApp alerts", "Document storage (1GB)", "Advanced analytics", "Priority support", "Early access to new features"].map((f) => (
                  <li key={f}><i className="ri-check-line"></i>{f}</li>
                ))}
              </ul>
              <div className="lp-plan-cta">
                <button className="lp-btn lp-btn-white" style={{ width: "100%", justifyContent: "center" }} onClick={() => navigate("/auth/signup")}>
                  Upgrade to Pro
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Languages ── */}
      <section className="lp-languages">
        <div className="lp-container">
          <div className="lp-lang-inner">
            <div className="lp-fade-up" style={{ minWidth: "260px" }}>
              <div className="lp-section-label">Multilingual</div>
              <h2 className="lp-section-title" style={{ fontSize: "32px" }}>Available in<br />6 Languages</h2>
              <p style={{ fontSize: "15px", color: "#64748b", marginTop: "12px", lineHeight: "1.6" }}>Switch anytime from Settings. PayFlow AI speaks your language.</p>
            </div>
            <div className="lp-lang-flags lp-fade-up">
              {[["🇬🇧", "English"], ["🇮🇳", "हिन्दी"], ["🇪🇸", "Español"], ["🇫🇷", "Français"], ["🇩🇪", "Deutsch"], ["🇯🇵", "日本語"]].map(([flag, name]) => (
                <div key={name} className="lp-lang-pill"><span style={{ fontSize: "20px" }}>{flag}</span>{name}</div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className="lp-testimonials" id="testimonials">
        <div className="lp-container">
          <div className="lp-fade-up" style={{ textAlign: "center" }}>
            <div className="lp-section-label">Reviews</div>
            <h2 className="lp-section-title">People love PayFlow AI</h2>
          </div>
          <div className="lp-testi-grid">
            {[
              { stars: 5, text: `"I used to miss my electricity bill every other month. Since using PayFlow AI, I haven't missed a single payment in 8 months. The auto-recurring feature is a game changer."`, name: "Rahul Sharma", role: "Software Engineer, Bangalore", letter: "R", bg: "linear-gradient(135deg,#4f46e5,#7c3aed)" },
              { stars: 5, text: `"Managing 12 EMIs was a nightmare in Excel. PayFlow AI tracks all of them, sends reminders 3 days before, and I get a trophy notification when the last EMI is done. Love it!"`, name: "Sneha Patil", role: "Business Owner, Pune", letter: "S", bg: "linear-gradient(135deg,#7c3aed,#db2777)" },
              { stars: 5, text: `"The calendar sync is brilliant. All my bills show up in Google Calendar automatically. My wife and I can both see what's due. Highly recommend for families."`, name: "Arjun Mehta", role: "Freelance Designer, Mumbai", letter: "A", bg: "linear-gradient(135deg,#0ea5e9,#6366f1)" },
            ].map((t) => (
              <div key={t.name} className="lp-testi-card lp-fade-up">
                <div className="lp-t-stars">{"★".repeat(t.stars)}</div>
                <p className="lp-t-text">{t.text}</p>
                <div className="lp-t-author">
                  <div className="lp-t-avatar" style={{ background: t.bg }}>{t.letter}</div>
                  <div>
                    <div className="lp-t-name">{t.name}</div>
                    <div className="lp-t-role">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="lp-faq" id="faq">
        <div className="lp-container">
          <div className="lp-fade-up" style={{ textAlign: "center" }}>
            <div className="lp-section-label">FAQ</div>
            <h2 className="lp-section-title">Frequently Asked Questions</h2>
            <p className="lp-section-sub" style={{ margin: "14px auto 0" }}>Everything you need to know about PayFlow AI.</p>
          </div>
          <div className="lp-faq-list">
            {faqs.map((faq, i) => (
              <div key={i} className={`lp-faq-item${openFaq === i ? " open" : ""}`}>
                <button className="lp-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  {faq.q}
                  <i className="ri-add-line lp-faq-icon"></i>
                </button>
                <div className="lp-faq-a">{faq.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Suggestions ── */}
      <section className="lp-suggestions" id="suggestions">
        <div className="lp-container">
          <div className="lp-fade-up" style={{ textAlign: "center" }}>
            <div className="lp-section-label">Community</div>
            <h2 className="lp-section-title">Share Your Suggestions</h2>
            <p className="lp-section-sub" style={{ margin: "14px auto 0" }}>Have an idea to make PayFlow AI better? We'd love to hear from you — no account needed.</p>
          </div>
          <div className="lp-sug-grid">
            {/* Form */}
            <div className="lp-fade-up">
              <form className="lp-sug-form" onSubmit={submitSuggestion}>
                <div className="lp-sug-form-title">
                  <i className="ri-lightbulb-line" style={{ color: "#f59e0b" }}></i>
                  Leave a suggestion
                </div>
                <div className="lp-field">
                  <label>Your Name</label>
                  <input
                    type="text"
                    placeholder="e.g. Rahul Sharma"
                    value={sugName}
                    onChange={(e) => setSugName(e.target.value)}
                    maxLength={100}
                  />
                </div>
                <div className="lp-field">
                  <label>Your Suggestion</label>
                  <textarea
                    placeholder="e.g. Add a dark mode option..."
                    rows={4}
                    value={sugMessage}
                    onChange={(e) => setSugMessage(e.target.value)}
                    maxLength={1000}
                  />
                  <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "4px", textAlign: "right" }}>
                    {sugMessage.length}/1000
                  </div>
                </div>
                {sugError && (
                  <div className="lp-sug-error">
                    <span><i className="ri-error-warning-line"></i> {sugError}</span>
                  </div>
                )}
                {sugSuccess && (
                  <div className="lp-sug-success">
                    <i className="ri-check-line" style={{ color: "#059669" }}></i>
                    <span>Thanks! Your suggestion has been submitted.</span>
                  </div>
                )}
                <button type="submit" className="lp-sug-submit" disabled={sugSubmitting} style={{ marginTop: "16px" }}>
                  {sugSubmitting
                    ? <><i className="ri-loader-4-line" style={{ animation: "spin 1s linear infinite" }}></i>Submitting...</>
                    : <><i className="ri-send-plane-line"></i>Submit Suggestion</>}
                </button>
              </form>
            </div>

            {/* Suggestions list */}
            <div className="lp-fade-up">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
                <p style={{ fontSize: "14px", fontWeight: 600, color: "#334155" }}>
                  <i className="ri-chat-3-line" style={{ color: "#4f46e5", marginRight: "6px" }}></i>
                  {suggestions.length} suggestion{suggestions.length !== 1 ? "s" : ""}
                </p>
              </div>
              {sugLoading ? (
                <div className="lp-sug-loading">
                  <i className="ri-loader-4-line" style={{ fontSize: "24px" }}></i>
                  <p style={{ marginTop: "8px", fontSize: "13px" }}>Loading suggestions...</p>
                </div>
              ) : suggestions.length === 0 ? (
                <div className="lp-sug-empty">
                  <i className="ri-lightbulb-line"></i>
                  <p style={{ fontSize: "14px" }}>No suggestions yet.</p>
                  <p style={{ fontSize: "12px", marginTop: "4px" }}>Be the first to share your idea!</p>
                </div>
              ) : (
                <div className="lp-sug-list">
                  {suggestions.map((s) => (
                    <div key={s.id} className="lp-sug-card">
                      <div className="lp-sug-card-header">
                        <div className="lp-sug-avatar">{s.name.slice(0, 2).toUpperCase()}</div>
                        <div>
                          <div className="lp-sug-name">{s.name}</div>
                          <div className="lp-sug-time">
                            {new Date(s.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                          </div>
                        </div>
                      </div>
                      <p className="lp-sug-message">{s.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="lp-cta" id="download">
        <div className="lp-container">
          <h2 className="lp-cta-title">Start tracking smarter today.<br />It's free.</h2>
          <p className="lp-cta-sub">Join thousands of users who never miss a payment.</p>
          <div className="lp-cta-actions">
            <button className="lp-store-btn" onClick={() => navigate("/home")}>
              <i className="ri-layout-grid-line" style={{ fontSize: "24px" }}></i>
              <div>
                <div className="lp-store-top">Open Dashboard</div>
                <div className="lp-store-main">Web App</div>
              </div>
            </button>
            <button className="lp-btn lp-btn-white" onClick={() => navigate("/auth/signup")}>
              <i className="ri-user-add-line"></i>Create Free Account
            </button>
          </div>
          <p style={{ color: "rgba(255,255,255,.5)", fontSize: "13px", marginTop: "24px", position: "relative", zIndex: 1 }}>
            No credit card required. Free forever plan available.
          </p>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="lp-footer">
        <div className="lp-container">
          <div className="lp-footer-grid">
            <div className="lp-footer-brand">
              <a href="#" className="lp-logo">
                <div className="lp-logo-icon"><i className="ri-money-dollar-circle-fill"></i></div>
                <span className="lp-logo-text">PayFlow <span>AI</span></span>
              </a>
              <p>The smart payment tracker that reminds you before bills are due, tracks subscriptions, and syncs with your calendar.</p>
            </div>
            <div>
              <div className="lp-footer-heading">Product</div>
              <ul className="lp-footer-links">
                <li><a href="#features" onClick={(e) => { e.preventDefault(); scrollTo("features"); }}>Features</a></li>
                <li><a href="#pricing" onClick={(e) => { e.preventDefault(); scrollTo("pricing"); }}>Pricing</a></li>
                <li><a href="#how-it-works" onClick={(e) => { e.preventDefault(); scrollTo("how-it-works"); }}>How it Works</a></li>
                <li><a href="#" onClick={(e) => { e.preventDefault(); navigate("/home"); }}>Web App</a></li>
              </ul>
            </div>
            <div>
              <div className="lp-footer-heading">Support</div>
              <ul className="lp-footer-links">
                <li><a href="#" onClick={(e) => { e.preventDefault(); navigate("/contact"); }}>Contact Us</a></li>
                <li><a href="#faq" onClick={(e) => { e.preventDefault(); scrollTo("faq"); }}>FAQ</a></li>
                <li><a href="mailto:support@wchatflowai.com">Email Support</a></li>
              </ul>
            </div>
            <div>
              <div className="lp-footer-heading">Legal</div>
              <ul className="lp-footer-links">
                <li><a href="#" onClick={(e) => { e.preventDefault(); navigate("/privacy"); }}>Privacy Policy</a></li>
                <li><a href="#" onClick={(e) => { e.preventDefault(); navigate("/terms"); }}>Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="lp-footer-bottom">
            <p className="lp-footer-copy">© 2026 PayFlow AI by Aryan Tech World. All rights reserved.</p>
            <div className="lp-footer-socials">
              <a href="https://twitter.com/payflowai" className="lp-social-icon" target="_blank" rel="noopener noreferrer" aria-label="Twitter"><i className="ri-twitter-x-line"></i></a>
              <a href="https://instagram.com/payflowai" className="lp-social-icon" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><i className="ri-instagram-line"></i></a>
              <a href="https://linkedin.com/company/payflowai" className="lp-social-icon" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><i className="ri-linkedin-line"></i></a>
              <a href="mailto:support@wchatflowai.com" className="lp-social-icon" aria-label="Email"><i className="ri-mail-line"></i></a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
